<template>
  <table class="entsys--table mobile--kipa-hide-header">
    <thead>
      <tr class="hidden-md-and-down">
        <th
          v-for="header in $auth.allowedHeaders(headersProp)"
          :key="header.text"
          class="text-left"
          style
        >
          {{ header.text }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-show="loading || items.length == 0" class="pa-0">
        <td colspan="99" class="pa-0">
          <v-progress-linear
            :active="loading"
            color="secondary"
            indeterminate
          />

          <div
            v-if="loading && items.length == 0"
            class="text-center py-4 muted--text"
          >
            {{ $t("common:loading.messsage", "Loading...", "Loading message") }}
          </div>
          <div
            v-else-if="!loading && items.length == 0"
            class="text-center py-4 muted--text"
          >
            {{
              $t(
                "common:NoResults.messsage",
                "No data available",
                "No results message"
              )
            }}
          </div>
        </td>
      </tr>
      <template v-for="(item, i) in items">
        <tr :key="item.id + '-row'">
          <td>
            <AppShowPersonName :person="item">
              <template v-if="item.dateOfBirth" #personNameAdditionalDetails>
                | {{ $dayjs.date(item.dateOfBirth) }}
              </template>
            </AppShowPersonName>
            &nbsp;
            <div class="hidden-lg-and-up" style="display:inline;">
              <AppShowCountry :country="item.representingCountry" /> &nbsp;
              {{
                item.representingRegionId ? item.representingRegion.name : ""
              }}
              &nbsp;
              <span>
                <AppShowPersonLicenceCategory :person="item" />
              </span>
            </div>
            <div v-if="item.address" class="muted--text">
              <small><AppShowAddress :address="item.address"/></small>
            </div>
            <div v-if="item.suspended || item.hasDebts" class="d-flex mt-2">
              <AppShowSuspension
                v-if="item.suspended"
                :suspension-reason="item.suspensionReason"
                class-list="ml-1"
              />

              <AppShowPersonDebtsBadge v-if="item.hasDebts" />
            </div>
          </td>
          <td class="hidden-md-and-down">
            <div class="d-flex align-center flex-wrap">
              <template
                v-for="({ name }, index) in showClubCountryFieldFormat(item)"
              >
                <div
                  v-if="name == 'representingClub'"
                  :key="index + item.id"
                  class="mr-2 my-1"
                  :style="{
                    color: item.currentSeasonLicenceValid
                      ? 'black'
                      : item.currentSeasonLicenceValid == null
                      ? ''
                      : 'grey'
                  }"
                >
                  {{ cutClubNameShort(item.representingClub) }}
                </div>

                <div
                  v-else-if="name == 'representingRegionId'"
                  :key="index + item.id"
                  class="mr-2 my-1"
                >
                  {{ item.representingRegion.name }}
                </div>

                <AppShowTopOrganisationLogo
                  v-else-if="name == 'topRegionOrganisation'"
                  :key="index + item.id"
                  :organisation="item.representingClub.topRegionOrganisation"
                  image-style="max-height: 20px;max-width: 54px"
                  class="mr-2 my-1"
                />

                <div
                  v-else-if="name == 'representingCountryId'"
                  :key="index + item.id"
                  class="mr-2 my-1"
                >
                  <AppShowCountry :country="item.representingCountry" />
                </div>
              </template>
            </div>
          </td>
          <td
            v-if="$auth.canAccessField('Person', 'licenceCategoryId', 'Any')"
            class="hidden-md-and-down"
          >
            <AppShowPersonLicenceCategory :person="item" />
          </td>
          <td
            v-if="$auth.canAccessField('Person', 'identifier', 'Any')"
            class="hidden-md-and-down"
          >
            <AppShowPersonIdentifier :person="item" />
            <div v-if="item.feiid" class="muted--text">
              <small>FEI: {{ item.feiid }}</small>
            </div>
          </td>
          <td
            v-if="
              $auth.canAccessField('Person', 'personProfQualifications', 'Any')
            "
            class="hidden-md-and-down"
            :style="{
              color: item.currentSeasonLicenceValid
                ? 'black'
                : item.currentSeasonLicenceValid == null
                ? ''
                : 'grey',
              maxWidth: '23em'
            }"
          >
            <AppShowPersonProfQualificationsList
              :person-prof-qualifications="
                item.personProfQualificationsValidCurrently
              "
            />
          </td>

          <td class="hidden-lg-and-up text-center">
            <button
              class="btn-focus"
              :aria-label="$t('common:expandButton.alt')"
              @click.prevent="expand($event, item.id)"
            >
              <v-icon color="primary">
                $iconCircleCaretDown
              </v-icon>
            </button>
          </td>
        </tr>

        <tr
          v-if="expanded.includes(item.id)"
          :key="item.id + '-expanded'"
          class="hidden-lg-and-up"
        >
          <td colspan="99" class="pa-0">
            <v-card color="strong">
              <v-card-text>
                <div v-if="item.representingClub" class="my-1">
                  <b>{{
                    $t(
                      "people:people.club.heading",
                      "Club",
                      "Heading of the people club on people page"
                    )
                  }}</b>

                  {{
                    item.representingClub.shortName ||
                      item.representingClub.name
                  }}
                  <template
                    v-if="
                      item.representingClub &&
                        item.representingClub.topRegionOrganisation &&
                        item.representingClub.topRegionOrganisation.logoPath
                    "
                  >
                    |
                    <AppShowTopOrganisationLogo
                      :organisation="
                        item.representingClub.topRegionOrganisation
                      "
                      image-style="max-height: 20px;max-width: 54px"
                    />
                  </template>
                </div>
                <div
                  v-if="
                    $auth.canAccessField('Person', 'licenceCategoryId', 'Any')
                  "
                  class="my-1 mr-1"
                >
                  <b>{{
                    $t(
                      "people:people.licenceCategory.heading",
                      "Licence category",
                      "Heading of the people licence category on people page"
                    )
                  }}</b>
                  &nbsp;
                  <AppShowPersonLicenceCategory :person="item" />
                </div>
                <div
                  v-if="$auth.canAccessField('Person', 'identifier', 'Any')"
                  class="my-1 mr-1"
                >
                  <b>{{
                    $t(
                      "people:people.licencenumber.heading",
                      "Licence number",
                      "Heading of the people licence number on people page"
                    )
                  }}</b>
                  &nbsp;
                  <AppShowPersonIdentifier :person="item" />
                  <div v-if="item.feiid" class="muted--text">
                    <small>FEI: {{ item.feiid }}</small>
                  </div>
                </div>
                <div
                  v-if="
                    $auth.canAccessField(
                      'Person',
                      'personProfQualifications',
                      'Any'
                    )
                  "
                  class="my-1 mr-1"
                >
                  <b>{{
                    $t(
                      "people:people.qualifications.heading",
                      "Professions",
                      "Heading of the people professions on people page"
                    )
                  }}</b>
                  &nbsp;
                  <AppShowPersonProfQualificationsList
                    :person-prof-qualifications="
                      item.personProfQualificationsValidCurrently
                    "
                  />
                </div>
              </v-card-text>
            </v-card>
          </td>
        </tr>

        <tr
          v-if="showBannerConditionally(i, 'people')"
          :key="'-ad-' + item.id"
          class="no-hover hidden-lg-and-up pa-0"
          style="border: 0;"
        >
          <td colspan="99" class="pa-0">
            <AppShowBanners
              placement-name="PEOPLE"
              :size="$vuetify.breakpoint.mdAndDown ? '150px' : undefined"
              :force-row="$vuetify.breakpoint.mdAndDown"
              class="py-4"
            />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>
<script>
import gql from "fraql";
import { useBannerHelpers } from "@composables/banner_helpers";
import AppShowBanners from "@/components/elements/banners/AppShowBanners.vue";

import AppShowCountry from "@AppShows/AppShowCountry.vue";
import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppShowPersonProfQualificationsList from "@AppShows/person/AppShowPersonProfQualificationsList.vue";
import AppShowPersonIdentifier from "@AppShows/person/AppShowPersonIdentifier.vue";
import AppShowPersonLicenceCategory from "@AppShows/person/AppShowPersonLicenceCategory.vue";
import AppShowTopOrganisationLogo from "@AppShows/organisation/AppShowTopOrganisationLogo.vue";
import AppShowAddress from "@AppShows/AppShowAddress.vue";
import AppShowSuspension from "@AppShows/AppShowSuspension.vue";
import AppShowPersonDebtsBadge from "@AppShows/person/AppShowPersonDebtsBadge.vue";
import { truncate } from "lodash-es";

export default {
  name: "SearchTemplatePeople",
  components: {
    AppShowPersonIdentifier,
    AppShowPersonLicenceCategory,
    AppShowPersonProfQualificationsList,
    AppShowPersonName,
    AppShowCountry,
    AppShowBanners,
    AppShowTopOrganisationLogo,
    AppShowAddress,
    AppShowPersonDebtsBadge,
    AppShowSuspension
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { showBannerConditionally } = useBannerHelpers();
    return { showBannerConditionally };
  },
  data() {
    return {
      expanded: []
    };
  },
  computed: {
    headersProp() {
      return [
        {
          value: "name",
          text: this.$t("people:fields.name.label", "Name", "Name field label")
        },
        {
          value: "clubName",
          text: this.$t(
            "people:fields.clubCountry.label",
            "Club / Country",
            "Name field label"
          )
        },
        {
          value: "licenceCategoryName",
          text: this.$t(
            "people:people.licenceCategory.heading",
            "Licence category",
            "Heading of the people licence category on people page"
          ),
          guardedByField: "Person.licenceCategoryId"
        },
        {
          value: "licenceNumber",
          text: this.$t(
            "people:people.licencenumber.heading",
            "Licence number",
            "Heading of the people licence number on people page"
          ),
          guardedByField: "Person.identifier"
        },
        {
          value: "",
          text: this.$t(
            "people:people.qualifications.heading",
            "Professions",
            "Heading of the people professions on people page"
          ),
          guardedByField: "Person.personProfQualifications"
        }
      ];
    }
  },
  fragments: {
    people: gql`
      fragment _ on Person {
        id
        ${AppShowPersonName.fragments.person}
        ${AppShowPersonIdentifier.fragments.person}
        ${AppShowPersonLicenceCategory.fragments.person}
        dateOfBirth
        name
        address {
          id
          ${AppShowAddress.fragments.addressFields}
        }
        ${AppShowSuspension.fragments.suspension}
        ${AppShowPersonDebtsBadge.fragments.personFields}
        feiid
        currentSeasonLicenceValid
        activeIdentifier
        licenceNumber
        licenceCategoryId
        licenceCategory @client {
          id
          name
        }
        representingRegionId
        representingRegion @client {
          id
          name
        }
        representingClub {
          id
          name
          shortName
          topRegionOrganisation {
            id
            logoPath
            ${AppShowTopOrganisationLogo.fragments.organisationFields}
          }
        }
        representingCountryId
        representingCountry @client {
          ${AppShowCountry.fragments.country}
        }
        
        personProfQualificationsValidCurrently {
          ${AppShowPersonProfQualificationsList.fragments.personProfQualification}
        }
      }
    `
  },
  methods: {
    expand(evt, id) {
      if (this.expanded.includes(id)) {
        this.expanded = this.expanded.filter(i => i != id);
      } else {
        this.expanded.push(id);
      }
      evt.target.classList.toggle("counter-rotate");
    },
    cutClubNameShort(club) {
      const name = club.shortName || club.name;
      if (name?.length > 18) return truncate(name, { length: 17 });
      return name;
    },
    showClubCountryFieldFormat(item) {
      const items = [
        {
          name: "representingClub",
          truthy: !!item.representingClub
        },
        {
          name: "representingRegionId",
          truthy: !!item.representingRegionId
        },
        {
          name: "topRegionOrganisation",
          truthy:
            item.representingClub &&
            item.representingClub.topRegionOrganisation &&
            item.representingClub.topRegionOrganisation.logoPath
        },
        {
          name: "representingCountryId",
          truthy: !!item.representingCountryId
        }
      ]
        .filter(x => x.truthy)
        .map(x => {
          return [x, { divider: true }];
        })
        .flat();

      return items.slice(0, items.length - 1);
    }
  }
};
</script>
